import { MEDIA_TIMELINE_POLICY } from "../MediaTimelineBar/mediaTimelineMath";
import type { NormalizedSeries } from "./seriesData";
import type { MediaTimelineSeriesDomain } from "../MediaTimelineBar/timelineTypes";

/** dataviz line mark: 2px, round joins. */
export const SERIES_LINE_WIDTH_PX = 2;
/** Half the stroke plus 2px of air so a line at the axis edge never touches the row border. */
export const SERIES_INSET_PX = 3;

export interface SeriesGeometry {
  gutter?: number;
  width: number;
  height: number;
  viewportStart: number;
  viewportDuration: number;
  domain: MediaTimelineSeriesDomain;
}

export interface SeriesPathCommand {
  op: "move" | "line";
  x: number;
  y: number;
}

interface Point {
  x: number;
  y: number;
}

/** Clip strokes, not samples: crossing times and slopes must survive an authored y axis. */
function clipSeriesPath(
  commands: readonly SeriesPathCommand[],
  top: number,
  bottom: number,
): SeriesPathCommand[] {
  const clipped: SeriesPathCommand[] = [];
  let previous: Point | undefined;
  let pen: Point | undefined;
  const inside = (point: Point) => point.y >= top && point.y <= bottom;
  for (const point of commands) {
    if (point.op === "move" || !previous) {
      pen = inside(point) ? point : undefined;
      if (pen) clipped.push({ op: "move", x: pen.x, y: pen.y });
    } else if ((previous.y < top && point.y < top) || (previous.y > bottom && point.y > bottom)) {
      pen = undefined;
    } else {
      const from = previous;
      const intersect = (endpoint: Point): Point => {
        if (inside(endpoint)) return endpoint;
        const y = Math.max(top, Math.min(bottom, endpoint.y));
        const fraction = (y - from.y) / (point.y - from.y);
        return { x: from.x + fraction * (point.x - from.x), y };
      };
      const start = intersect(previous);
      const end = intersect(point);
      if (!pen || pen.x !== start.x || pen.y !== start.y) {
        clipped.push({ op: "move", x: start.x, y: start.y });
      }
      clipped.push({ op: "line", x: end.x, y: end.y });
      pen = inside(point) ? end : undefined;
    }
    previous = point;
  }
  return clipped;
}

/** First index whose time is not below `time` (times ascending). */
export function lowerBoundTime(times: Float64Array, time: number): number {
  let low = 0;
  let high = times.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (times[middle]! < time) low = middle + 1;
    else high = middle;
  }
  return low;
}

/**
 * Projects one series into stroke commands in local CSS pixels. Only samples that can affect
 * the viewport are visited: the visible range plus one neighbour on each side, so a line keeps
 * its slope (or its held value) across the edges. Samples denser than the pixel grid collapse
 * into a per-column envelope (first, min, max, last), which preserves every spike a full-scale
 * plot would show without walking a path through thousands of sub-pixel points. A `NaN` value
 * ends the current run; nothing is drawn across the gap. Inside one column a gap only decides
 * whether the column's envelope connects onward — the envelope itself stays one shape, so dense
 * missing values cannot turn a column into hundreds of one-sample runs. The last sample holds
 * flat for the series' `holdSeconds`, whatever its interpolation.
 */
export function buildSeriesPath(
  series: NormalizedSeries,
  geometry: SeriesGeometry,
): SeriesPathCommand[] {
  const { width, height, viewportStart, viewportDuration, domain } = geometry;
  const gutter = geometry.gutter ?? MEDIA_TIMELINE_POLICY.leftGutterPx;
  const usableWidth = width - gutter;
  const plotHeight = height - 2 * SERIES_INSET_PX;
  if (!(usableWidth > 0) || !(plotHeight > 0) || !(viewportDuration > 0)) return [];
  const { times, values } = series;
  if (times.length === 0) return [];

  const xOf = (time: number) => gutter + ((time - viewportStart) / viewportDuration) * usableWidth;
  const span = domain.max - domain.min;
  const yOf = (value: number) => {
    if (!(span > 0)) return height / 2;
    const fraction = (value - domain.min) / span;
    return SERIES_INSET_PX + (1 - fraction) * plotHeight;
  };

  const first = Math.max(0, lowerBoundTime(times, viewportStart) - 1);
  const last = Math.min(times.length - 1, lowerBoundTime(times, viewportStart + viewportDuration));

  const commands: SeriesPathCommand[] = [];
  const run: Point[] = [];
  const emitRun = () => {
    if (run.length === 0) return;
    let previous = run[0]!;
    commands.push({ op: "move", x: previous.x, y: previous.y });
    for (let index = 1; index < run.length; index += 1) {
      const point = run[index]!;
      if (series.interpolation === "step" && point.x !== previous.x && point.y !== previous.y) {
        commands.push({ op: "line", x: point.x, y: previous.y });
      }
      commands.push({ op: "line", x: point.x, y: point.y });
      previous = point;
    }
    run.length = 0;
  };

  // Per-column envelope of the samples sharing one CSS pixel column.
  let column = Number.NaN;
  let columnX = 0;
  let firstY = 0;
  let minY = 0;
  let maxY = 0;
  let lastY = 0;
  let columnCount = 0;
  // The column's latest sample is a gap: its envelope ends the run at that gap. Two variables so
  // the x stays an unboxed double across a million samples.
  let columnEndsInGap = false;
  let columnGapX = 0;
  // A held value ends where the gap sample sits, not at the previous sample.
  const endRunAtGap = (gapX: number) => {
    if (series.interpolation === "step" && run.length > 0) {
      const held = run[run.length - 1]!;
      run.push({ x: gapX, y: held.y });
    }
    emitRun();
  };
  const flushColumn = () => {
    if (columnCount === 0) return;
    if (columnCount === 1) {
      run.push({ x: columnX, y: firstY });
    } else {
      run.push({ x: columnX, y: firstY });
      if (minY !== firstY) run.push({ x: columnX, y: minY });
      if (maxY !== minY) run.push({ x: columnX, y: maxY });
      if (lastY !== maxY) run.push({ x: columnX, y: lastY });
    }
    columnCount = 0;
    column = Number.NaN;
    if (columnEndsInGap) {
      columnEndsInGap = false;
      endRunAtGap(columnGapX);
    }
  };

  for (let index = first; index <= last; index += 1) {
    const value = values[index]!;
    const x = xOf(times[index]!);
    const nextColumn = Math.floor(x);
    if (Number.isNaN(value)) {
      if (nextColumn === column) {
        columnEndsInGap = true;
        columnGapX = x;
        continue;
      }
      flushColumn();
      endRunAtGap(x);
      continue;
    }
    const y = yOf(value);
    if (nextColumn !== column) {
      flushColumn();
      column = nextColumn;
      columnX = x;
      firstY = minY = maxY = lastY = y;
      columnCount = 1;
      continue;
    }
    columnCount += 1;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    lastY = y;
    // Data after a sub-pixel gap joins the same envelope, and the column connects onward again.
    columnEndsInGap = false;
  }
  flushColumn();
  const finalIndex = times.length - 1;
  if (last === finalIndex && series.holdSeconds > 0 && !Number.isNaN(values[finalIndex]!)) {
    run.push({ x: xOf(times[finalIndex]! + series.holdSeconds), y: yOf(values[finalIndex]!) });
  }
  emitRun();
  return clipSeriesPath(commands, SERIES_INSET_PX, height - SERIES_INSET_PX);
}
